import React, { useState, useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faRightLeft, faCheck } from "@fortawesome/free-solid-svg-icons";

import RouteHeader from "../common/RouteHeader";
import PanelWeekNav from "./PanelWeekNav";

import "./PanelHeader.css";
import "./PanelWeekReport.css";

/**
 * Cabeçalho do relatório semanal — a faixa `.ptop` do hi-fi.
 *
 * <p>🔴 O TOPO É CABEÇALHO DE PÁGINA, NÃO TOPO DO CORPO (PO, 2026-09-08). Eu
 * vinha renderizando crumb, sujeito e semana dentro do corpo, e por isso a
 * página nunca parecia o hi-fi por mais CSS que eu escrevesse: o portal tem uma
 * FAIXA de cabeçalho, e o conteúdo dela entra por {@code setPageHeader} — é o
 * mesmo caminho do {@code PanelPrivateHeader}.
 *
 * <p>⚠️ O crumb é `Painel / Relatório da semana`, não o default `Reeduca /
 * Painel`: o módulo é o painel, mas a página tem nome próprio.
 *
 * <p>🔴 <b>A SEMANA VIROU NAVEGADOR</b> (CD, 2026-09-16 · redline v2 §1), e é a
 * mesma barra `‹ Semana 36 · 31 ago – 6 set ›` do painel da turma. Ela paga três
 * dívidas de uma vez: zero vocabulário novo (controle que o professor já usa),
 * <b>renomeia a semana lida</b>, e libera a faixa da obrigação de ficar no topo
 * — que era o que a fazia grande e primeira.
 *
 * <p>↺ Isto SUPERA o rótulo mudo de §6.1 do redline de agosto ("qualquer
 * aparência de controle prometeria uma navegação que a página não tem"). A
 * página passou a ter navegação quando a faixa entrou; o que não caiu foi o
 * ARGUMENTO, e ele volta como guarda: <b>o navegador não oferece semana sem
 * fotografia</b> — a seta para a frente desabilita na última semana fechada.
 *
 * <p>✂️ E o `hideWeek` morreu com ele: a barra <b>é</b> o rótulo, então não há
 * mais o caso de esconder um para não repetir o outro. Ela nomeia a semana lida
 * inclusive quando não há fotografia — chegada por `?week=37` diz
 * <i>Semana 37 · 7 – 13 set · em curso</i>. A barra é o RÓTULO DA PÁGINA, não um
 * índice do que existe: se mostrasse a 36 enquanto o corpo fala da 37, a semana
 * lida ficaria sem nome em lugar nenhum — o defeito que ela existe para consertar.
 */
const PanelWeekReportHeader = ({ report, schoolName, canSelect, canSwitchNature,
                                classes, selectedClassId, onPickClass, onSwitchNature,
                                onPrevWeek, onNextWeek, canGoNext, canGoPrev }) => {
	const { t } = useTranslation();
	/* ⚠️ O menu é do CABEÇALHO, e o desenho é o mesmo do `PanelHeader` da turma
	   (`.pnl-header-dd`): mesma pergunta ("qual turma?"), mesmo lugar da tela.
	   Um segundo desenho ensinaria dois menus pro mesmo ato.
	   ✂️ SEM busca e sem seção de arquivadas, ao contrário do irmão: aqui a lista
	   já vem filtrada em ativas, e o relatório de escola com 40 turmas não é o
	   caso do piloto. Quando for, a busca vem de lá pronta. */
	const [open, setOpen] = useState(false);
	const wrapRef = useRef(null);

	useEffect(() => {
		if (!open) return;
		const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
		document.addEventListener('mousedown', onDown);
		return () => document.removeEventListener('mousedown', onDown);
	}, [open]);

	/* ⚠️ MESMA regra dos outros cabeçalhos do painel (`PanelHeader`,
	   `PanelPrivateHeader`): escola no L1 só em multi-escola, e aí "Painel"
	   ganha `pnl-crumb-hide-mobile`. `schoolName` já chega null exatamente
	   quando há uma escola só — a condição não foi inventada, foi lida.
	   ⚠️ Eu tinha montado um crumb próprio de dois níveis, e ele divergia de
	   TODAS as outras páginas do painel. */
	const crumbs = [
		...(schoolName
			? [{ label: schoolName },
			   { label: <Trans i18nKey="header.navigation-titles.panel">Painel</Trans>, className: 'pnl-crumb-hide-mobile' }]
			: [{ label: <Trans i18nKey="header.navigation-titles.panel">Painel</Trans> }]),
		{ label: <Trans i18nKey="panel.report.crumb">Relatório da semana</Trans> },
	];

	/* 🔴 O SUJEITO É O PROFESSOR na aula particular (PO, 2026-09-09).
	 *
	 * Aqui estava escrito que a assimetria "seletor em turma, texto em
	 * particular" era intencional (§6.2), "porque a particular é agregada e não
	 * há o que escolher". A premissa vale com UM professor particular na escola.
	 * Com dois, havia o que escolher e a tela não oferecia: o cliente mandava
	 * `firstPrivateTeacherId` e o relatório do segundo ficava sem caminho.
	 *
	 * ⚠️ O `Prof.` é prefixado AQUI, pela chave i18n — o servidor manda o nome
	 * cru porque a forma muda com o idioma.
	 *
	 * ⚠️ O texto fixo "Aulas particulares" sobrevive num caso só: professor sem
	 * nome resolvido. Melhor um rótulo genérico que um "Prof." órfão. */
	/* 🔴 O CABEÇALHO TAMBÉM É SHIM (13/09). Esta rota era a ÚNICA do painel sem
	   `headerComponent` no config — turma, aula particular e aluno todas têm —
	   e o comentário de lá justificava dizendo "mesmo caminho do PanelPrivate",
	   que era falso: o `PanelPrivate` tem os DOIS. Shim e `setPageHeader` não
	   são alternativos; o shim cobre o intervalo ATÉ o fetch.
	   Sem ele a rota caía no terceiro nível do `MainPage` — `RouteHeader` puro,
	   sem shimmer —, e o sujeito e a semana APARECIAM DE ESTALO quando o dado
	   chegava, com o corpo já em skeleton.
	   ⇒ Os slots que dependem do fetch shimmeram. Régua do `PanelStudentHeader`:
	   "mostrar apagado o que você sabe é pior que shimmerar o que você não sabe". */
	const isLoading = !report;
	const shimmer = (w, h) => (
		<span className="sk" aria-hidden="true"
			style={{ width: w, height: h, borderRadius: 4, display: 'inline-block' }} />
	);

	const subjectLabel = isLoading ? null : (report.classType === 'PRIVATE'
		? (report.subjectName
			? t('panel.private.teacher-name', { name: report.subjectName })
			: t('panel.report.private-subject'))
		: report.subjectName);

	/* 🔴 O MENU ABRE POR DOIS MOTIVOS (PO, 2026-09-12). `canSelect` é
	   `itens > 1` — a régua de sempre, "combo de um só confirma o que já está
	   na tela". Mas a TROCA DE NATUREZA não depende disso: um professor com
	   UMA turma e UMA aula particular tem `canSwitchNature` e não teria menu.
	   Pendurar a troca aqui sem abrir o menu pra ele apagaria o caminho até o
	   relatório de particulares — não a linha, o acesso.
	   ⚠️ A régua continua de pé: com o item de troca dentro, o menu deixa de
	   ser um combo de um. */
	const hasMenu = !isLoading && (canSelect || canSwitchNature);

	const subject = isLoading ? (
		/* ⚠️ 180×22: a medida do irmão `PanelPrivateHeader`, pra o shimmer do
		   título ter o mesmo peso em todas as telas do painel. */
		shimmer(180, 22)
	) : hasMenu ? (
		<button type="button" className="pnl-header-h1sel" onClick={() => setOpen(v => !v)}>
			<span className="pnl-header-h1sel-txt">{subjectLabel}</span>
			<FontAwesomeIcon icon={faChevronDown} className="pnl-header-cv" />
		</button>
	) : (
		<span className="pnl-header-h1static">{subjectLabel}</span>
	);

	/* 🔴 O link fica AO LADO do sujeito, na mesma linha — é a `.subjrow` do
	   hi-fi (flex, gap 11px). Eu o tinha posto ABAIXO do título ao mover o topo
	   para a faixa, e aí ele virou uma terceira linha que o desenho não tem. */
	/* ✂️ O LINK DE TROCA SAIU DAQUI (PO, 2026-09-12) e virou item do menu, sob
	   um fio — a peça que o `PanelHeader` já usava pras turmas arquivadas.
	   No celular ele gastava uma LINHA inteira do cabeçalho: o embrulho era
	   `flex-wrap: wrap`, e com `Matemática Avançada` ocupando a faixa o botão
	   caía sozinho na de baixo.
	   ⚠️ Divergimos do hi-fi de propósito ("o toggle SAI — o sujeito é o
	   título; a troca de natureza é link discreto"): ele resolveu a largura do
	   DESKTOP, onde título e link cabem lado a lado. No celular não cabem.
	   ⚠️ Vale nas DUAS larguras. Manter o link no desktop e o item no menu no
	   celular seria a mesma ação em dois lugares — e a próxima mudança pegaria
	   só um deles.

	   🔴 E o EMBRULHO `.pnl-wr-subjrow` saiu junto, não por limpeza: com um
	   filho só ele ESTRANGULAVA o título. Ele era `inline-flex; min-width: 0`,
	   e o `max-width: 100%` do botão passou a resolver contra uma caixa que
	   encolhe ao mínimo em vez de contra o `.rh-title-slot`, que é bloco de
	   largura cheia. `Prof. Ana Souza` truncava em `Prof. Ana So…` com a linha
	   inteira vazia à direita.
	   ⇒ O título agora vai DIRETO pro `RouteHeader`, igual ao `PanelHeader` e
	   ao `PanelPrivateHeader`. Três cabeçalhos, uma montagem. */

	return (
		/* ⚠️ `pnl-wr-header` é MODIFICADOR, não classe nova de layout: o
		   `.pnl-header` é compartilhado com a Visão geral, o painel da turma, o
		   das particulares e o do aluno — e só AQUI o título é longo o bastante
		   pra brigar com a caixa da semana. Mesma convenção do `pnls-header`. */
		<div className="pnl-header pnl-wr-header">
			<div className="pnl-header-titlewrap" ref={wrapRef}>
				<RouteHeader crumbs={crumbs} title={subject} />

				{open && hasMenu && (
					<div className="pnl-header-dd">
						{canSelect && (classes || []).map(c => (
							<div
								key={c.id}
								className={"pnl-header-ddi" + (c.id === selectedClassId ? " pnl-header-ddi-on" : "")}
								onClick={() => { onPickClass(c.id); setOpen(false); }}
							>
								{c.name}
								{c.id === selectedClassId && (
									<FontAwesomeIcon icon={faCheck} className="pnl-header-ddi-ck" />
								)}
							</div>
						))}
						{/* A TROCA DE NATUREZA — item de ação, não uma das opções, e é
						    o fio que diz isso. Tinta mais fraca (`ddi-alt`) pela mesma
						    razão: ela não responde "qual?", responde "e o outro lado?".
						    ⚠️ Só quando o professor TEM os dois lados
						    (`entry.hasGroup && entry.hasPrivate`) — sem aula particular
						    o item não nasce, e sem turma também não. */}
						{canSwitchNature && (
							<div className="pnl-header-ddsec">
								<div
									className="pnl-header-ddi pnl-header-ddi-alt"
									onClick={() => { onSwitchNature(); setOpen(false); }}
								>
									<FontAwesomeIcon icon={faRightLeft} />
									{report?.classType === 'PRIVATE'
										? <Trans i18nKey="panel.report.switch-to-classes">Trocar para turmas</Trans>
										: <Trans i18nKey="panel.report.switch-to-private">Trocar para aulas particulares</Trans>}
								</div>
							</div>
						)}
					</div>
				)}
			</div>

			{isLoading ? (
				<div className="pnl-wr-wknav">
					<div className="pnl-wr-week">
						<span className="pnl-wr-week-n">{shimmer(74, 13)}</span>
						<span className="pnl-wr-week-range">{shimmer(92, 10)}</span>
					</div>
				</div>
			) : (
				<PanelWeekNav
					weekNumber={report.weekNumber}
					weekStart={report.weekStart}
					weekEnd={report.weekEnd}
					canGoNext={canGoNext}
					canGoPrev={canGoPrev}
					onPrev={onPrevWeek}
					onNext={onNextWeek}
				/>
			)}
		</div>
	);
};

export default PanelWeekReportHeader;
